import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { entityService } from '../services/entityService.js';
import { medicalRecordService } from '../services/medicalRecordService.js';
import ConsultationForm from './ConsultationForm.js';
import { formatPhone, SPECIES_LABEL } from '../utils/formatters.js';
import ConfirmModal from './ConfirmModal.js';
import placeholderImage from '../images/placeholder.png';
import './MedicalRecord.css';



const formatCPF = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
};

/* ───────────────────────── Owner Modal ─────────────────────── */
function OwnerModal({ isOpen, onClose, owner, entityId, onSaved }) {
  const [form, setForm] = useState({ name: '', cpf: '', phone: '', email: '', address: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (owner) {
      setForm({ name: owner.name || '', cpf: formatCPF(owner.cpf || ''), phone: formatPhone(owner.phone || ''), email: owner.email || '', address: owner.address || '' });
    } else {
      setForm({ name: '', cpf: '', phone: '', email: '', address: '' });
    }
  }, [owner, isOpen]);

  if (!isOpen) return null;

  const inputSt = { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', background: '#fff' };
  const labelSt = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: name === 'phone' ? formatPhone(value) : name === 'cpf' ? formatCPF(value) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    const payload = { ...form, phone: form.phone.replace(/\D/g, ''), cpf: form.cpf.replace(/\D/g, ''), entityId };
    const result = owner
      ? await medicalRecordService.updateOwner(owner.id, payload)
      : await medicalRecordService.createOwner(payload);
    setSaving(false);
    if (result.success) {
      toast.success(owner ? 'Tutor atualizado!' : 'Tutor criado!');
      onSaved && onSaved(result.owner);
      onClose();
    } else {
      toast.error(result.error || 'Erro ao salvar tutor');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f0f0f0' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111' }}>{owner ? 'Editar Tutor' : 'Novo Tutor'}</h2>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>{owner ? 'Atualize os dados do tutor' : 'Preencha os dados do responsável'}</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', fontSize: '18px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelSt}>Nome *</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} required style={inputSt} placeholder="Ex: João Silva" />
            </div>
            <div>
              <label style={labelSt}>CPF</label>
              <input type="text" name="cpf" value={form.cpf} onChange={handleChange} style={inputSt} placeholder="000.000.000-00" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelSt}>Telefone</label>
              <input type="tel" name="phone" value={form.phone} onChange={handleChange} style={inputSt} placeholder="(11) 99999-9999" />
            </div>
            <div>
              <label style={labelSt}>E-mail</label>
              <input type="email" name="email" value={form.email} onChange={handleChange} style={inputSt} placeholder="email@exemplo.com" />
            </div>
          </div>
          <div>
            <label style={labelSt}>Endereço</label>
            <input type="text" name="address" value={form.address} onChange={handleChange} style={inputSt} placeholder="Rua, número, bairro..." />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ padding: '10px 20px', border: '1.5px solid #e5e7eb', borderRadius: '10px', background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '10px 24px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Salvando...' : owner ? 'Salvar Alterações' : 'Cadastrar Tutor'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Pet Modal ─────────────────────── */
function PetModal({ isOpen, onClose, pet, ownerId, entityId, onSaved }) {
  const EMPTY = { name: '', species: '', breed: '', sex: '', birthday: '', weight: '', color: '', neutered: false, neuteredDate: '' };
  const [form, setForm] = useState({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);

  useEffect(() => {
    if (pet) {
      setForm({
        name: pet.name || '', species: pet.species || '', breed: pet.breed || '',
        sex: pet.sex || '', birthday: pet.birthday ? dayjs(pet.birthday).format('YYYY-MM-DD') : '',
        weight: pet.weight || '', color: pet.color || '',
        neutered: pet.neutered || false,
        neuteredDate: pet.neuteredDate ? dayjs(pet.neuteredDate).format('YYYY-MM-DD') : '',
      });
      setPhotoPreview(pet.photo || null);
    } else {
      setForm({ ...EMPTY });
      setPhotoPreview(null);
    }
    setPhotoFile(null);
  }, [pet, isOpen]);

  if (!isOpen) return null;

  const inputSt = { width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit', background: '#fff' };
  const labelSt = { display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) { toast.error('Formato inválido. Use JPG, PNG ou WEBP.'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande. Máximo 5MB.'); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome é obrigatório'); return; }
    setSaving(true);
    const raw = { ...form, ownerId, entityId };
    const payload = Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== ''));
    let result = pet
      ? await medicalRecordService.updatePet(pet.id, payload)
      : await medicalRecordService.createPet(payload);

    if (!result.success) {
      toast.error(result.error || 'Erro ao salvar pet');
      setSaving(false);
      return;
    }

    // Upload de foto se selecionada
    let savedPet = result.pet;
    if (photoFile && savedPet?.id) {
      const photoResult = await medicalRecordService.uploadPetPhoto(photoFile, savedPet.id, entityId);
      if (photoResult.success) {
        savedPet = { ...savedPet, photo: photoResult.photoUrl };
      } else {
        toast.error(photoResult.error || 'Pet salvo, mas erro ao enviar foto');
      }
    }

    setSaving(false);
    toast.success(pet ? 'Pet atualizado!' : 'Pet criado!');
    onSaved && onSaved(savedPet);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #f0f0f0' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#111' }}>{pet ? 'Editar Paciente' : 'Novo Paciente'}</h2>
            <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#9ca3af' }}>{pet ? 'Atualize os dados do paciente' : 'Preencha os dados do paciente'}</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f3f4f6', cursor: 'pointer', width: '32px', height: '32px', borderRadius: '50%', fontSize: '18px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '14px' }}>

          {/* Foto */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => photoInputRef.current?.click()} title="Clique para alterar a foto">
              {photoPreview ? (
                <img src={photoPreview} alt="Foto do pet" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--color-main)' }} onError={(e) => { e.target.src = placeholderImage; }} />
              ) : (
                <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(113,101,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed var(--color-main)' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--color-main)" strokeWidth="1.5"><circle cx="12" cy="8" r="5" /><path d="M12 14c-5 0-8 2.5-8 4v2h16v-2c0-1.5-3-4-8-4z" /></svg>
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 0, right: 0, width: 24, height: 24, borderRadius: '50%', background: 'var(--color-main)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
              </div>
            </div>
            <span style={{ fontSize: '11px', color: '#9ca3af' }}>Clique para adicionar foto</span>
            <input ref={photoInputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" style={{ display: 'none' }} onChange={handlePhotoChange} />
          </div>

          {/* Nome + Espécie */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelSt}>Nome *</label>
              <input type="text" name="name" value={form.name} onChange={handleChange} required style={inputSt} placeholder="Ex: Thor" />
            </div>
            <div>
              <label style={labelSt}>Espécie</label>
              <select name="species" value={form.species} onChange={handleChange} style={inputSt}>
                <option value="">Selecionar...</option>
                {Object.entries(SPECIES_LABEL).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Raça + Sexo */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelSt}>Raça</label>
              <input type="text" name="breed" value={form.breed} onChange={handleChange} style={inputSt} placeholder="Ex: Golden Retriever" />
            </div>
            <div>
              <label style={labelSt}>Sexo</label>
              <select name="sex" value={form.sex} onChange={handleChange} style={inputSt}>
                <option value="">Selecionar...</option>
                <option value="male">Macho</option>
                <option value="female">Fêmea</option>
              </select>
            </div>
          </div>

          {/* Nascimento + Peso + Cor */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div>
              <label style={labelSt}>Nascimento</label>
              <input type="date" name="birthday" value={form.birthday} onChange={handleChange} style={inputSt} />
            </div>
            <div>
              <label style={labelSt}>Peso (kg)</label>
              <input type="number" name="weight" value={form.weight} onChange={handleChange} step="0.1" style={inputSt} />
            </div>
            <div>
              <label style={labelSt}>Cor/Pelagem</label>
              <input type="text" name="color" value={form.color} onChange={handleChange} style={inputSt} />
            </div>
          </div>

          {/* Castrado */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input type="checkbox" id="pm-neutered" name="neutered" checked={form.neutered} onChange={handleChange} style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-main)' }} />
            <label htmlFor="pm-neutered" style={{ fontSize: '13px', color: '#374151', cursor: 'pointer' }}>Castrado(a)</label>
          </div>
          {form.neutered && (
            <div>
              <label style={labelSt}>Data de Castração</label>
              <input type="date" name="neuteredDate" value={form.neuteredDate} onChange={handleChange} style={inputSt} />
            </div>
          )}
        </form>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ padding: '10px 20px', border: '1.5px solid #e5e7eb', borderRadius: '10px', background: '#fff', color: '#374151', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Cancelar</button>
          <button onClick={handleSubmit} disabled={saving} style={{ padding: '10px 24px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Salvando...' : pet ? 'Salvar Alterações' : 'Cadastrar Paciente'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────── Consultation Preview ──────────────────── */
const GENERAL_CONDITION_LABEL = { good: 'Bom', fair: 'Regular', poor: 'Ruim', critical: 'Crítico' };
const HYDRATION_LABEL = { normal: 'Normal', mild: 'Leve (< 5%)', moderate: 'Moderada (5-10%)', severe: 'Grave (> 10%)' };
const NEEDS_RETURN_LABEL = { yes: 'Sim', no: 'Não', if_needed: 'Se necessário' };
const PRIORITY_LABEL = { low: 'Baixa', normal: 'Normal', high: 'Alta', urgent: 'Urgente' };
const EXAM_LABELS = { hemograma: 'Hemograma', bioquimico: 'Bioquímico', raio_x: 'Raio-X', ultrassom: 'Ultrassom', urinanalise: 'Urinálise', parasitologico: 'Parasitológico' };

function PreviewRow({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ marginBottom: '12px' }}>
      <p style={{ margin: '0 0 2px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</p>
      <p style={{ margin: 0, fontSize: '14px', color: '#1f2937' }}>{value}</p>
    </div>
  );
}

function ConsultationPreview({ consultation, onEdit, onFinalize, onBack, finalizing, onDelete }) {
  const c = consultation;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const exams = Array.isArray(c.requestedExams)
    ? c.requestedExams.map(e => EXAM_LABELS[e] || e).join(', ')
    : null;

  const isEmpty = !c.chiefComplaint && !c.historyAndObservations &&
    !c.weight && !c.temperature && !c.heartRate && !c.respiratoryRate && !c.generalCondition && !c.hydrationLevel && !c.physicalExamNotes &&
    !c.clinicalDiagnosis && !exams &&
    !c.generalGuidelines &&
    !c.needsReturn && !c.returnDeadline && !c.returnNotes &&
    !(c.medications?.length > 0) &&
    !(c.Documents?.length > 0) &&
    !c.internalNotes;

  return (
    <div className="mr-container">
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={onDelete}
        title="Remover Consulta"
        message="Tem certeza que deseja remover esta consulta? Todos os medicamentos, documentos vinculados e o agendamento associado serão removidos permanentemente."
        confirmText="Remover"
        type="danger"
      />
      <div className="mr-page-header">
        <div className="mr-breadcrumb">
          <button className="mr-breadcrumb-link" onClick={onBack}>Voltar</button>
          <span className="mr-breadcrumb-sep">›</span>
          <span className="mr-breadcrumb-current">Consulta — {dayjs(c.createdAt).format('DD/MM/YYYY')}</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {c.isDraft && (
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FEF3C7', padding: '3px 10px', borderRadius: '20px' }}>Rascunho</span>
          )}
          {c.isDraft && (
            <button
              className="btn-primary"
              style={{ padding: '9px 20px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
              onClick={onFinalize}
              disabled={finalizing}
            >
              {finalizing ? 'Finalizando...' : <><span>✓</span><span>Finalizar</span></>}
            </button>
          )}
          <button
            className="btn-secondary"
            style={{ padding: '9px 20px', fontSize: '13px' }}
            onClick={onEdit}
          >
            Editar
          </button>
          {onDelete && (
            <button
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', border: '1.5px solid #FEE2E2', borderRadius: '10px', background: '#FFF5F5', color: '#DC2626', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
              onClick={() => setShowDeleteConfirm(true)}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
              Remover
            </button>
          )}
        </div>
      </div>

      {c.scheduleId && (() => {
        const iscanceled = c.schedule?.status === 'canceled';
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            background: iscanceled ? '#FFF5F5' : '#EFF6FF',
            border: `1px solid ${iscanceled ? '#FEE2E2' : '#BFDBFE'}`,
            borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={iscanceled ? '#DC2626' : '#2563EB'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: iscanceled ? '#DC2626' : '#1D4ED8' }}>Consulta vinculada ao agendamento <span style={{ fontWeight: '400' }}>#{c.scheduleId}</span></p>
              {c.schedule && (
                <p style={{ margin: '3px 0 0', fontSize: '12px', color: iscanceled ? '#EF4444' : '#3B82F6' }} title={c.schedule.dateTime}>
                  {c.schedule.dateTime && new Date(c.schedule.dateTime).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  {c.schedule.durationMinutes && <> &mdash; {c.schedule.durationMinutes} min</>}
                  {c.schedule.status && <> &mdash; {({ scheduled: 'Agendado', confirmed: 'Confirmado', in_progress: 'Em atendimento', completed: 'Finalizado', canceled: 'Cancelado', no_show: 'Não compareceu' })[c.schedule.status] || c.schedule.status}</>}
                </p>
              )}
            </div>
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {isEmpty && (
          <div style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', gap: '12px', color: '#9ca3af' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
            <p style={{ margin: 0, fontWeight: '600', fontSize: '15px', color: '#6b7280' }}>Consulta sem informações preenchidas</p>
            <p style={{ margin: 0, fontSize: '13px' }}>Clique em "Editar" para preencher os dados desta consulta.</p>
          </div>
        )}
        {/* Anamnese */}
        {(c.chiefComplaint || c.historyAndObservations) && (
          <div style={{ gridColumn: '1 / -1', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Anamnese</p>
            <PreviewRow label="Queixa Principal" value={c.chiefComplaint} />
            <PreviewRow label="Histórico e Observações" value={c.historyAndObservations} />
          </div>
        )}

        {/* Exame Físico */}
        {(c.weight || c.temperature || c.heartRate || c.respiratoryRate || c.generalCondition || c.hydrationLevel || c.physicalExamNotes) && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Exame Físico</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <PreviewRow label="Peso (kg)" value={c.weight} />
              <PreviewRow label="Temperatura (°C)" value={c.temperature} />
              <PreviewRow label="Freq. Cardíaca (bpm)" value={c.heartRate} />
              <PreviewRow label="Freq. Respiratória" value={c.respiratoryRate} />
              <PreviewRow label="Estado Geral" value={GENERAL_CONDITION_LABEL[c.generalCondition]} />
              <PreviewRow label="Hidratação" value={HYDRATION_LABEL[c.hydrationLevel]} />
            </div>
            <PreviewRow label="Observações" value={c.physicalExamNotes} />
          </div>
        )}

        {/* Diagnóstico */}
        {(c.clinicalDiagnosis || exams) && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Diagnóstico</p>
            <PreviewRow label="Diagnóstico Clínico" value={c.clinicalDiagnosis} />
            <PreviewRow label="Exames Solicitados" value={exams} />
          </div>
        )}

        {/* Conduta */}
        {c.generalGuidelines && (
          <div style={{ gridColumn: '1 / -1', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Conduta</p>
            <PreviewRow label="Orientações Gerais" value={c.generalGuidelines} />
          </div>
        )}

        {/* Retorno */}
        {(c.needsReturn || c.returnDeadline || c.returnNotes) && (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Retorno</p>
            <PreviewRow label="Precisa Retornar?" value={NEEDS_RETURN_LABEL[c.needsReturn]} />
            <PreviewRow label="Prazo" value={c.returnDeadline ? `${c.returnDeadline} ${Number(c.returnDeadline) === 1 ? 'dia' : 'dias'} (até ${dayjs(c.createdAt).add(Number(c.returnDeadline), 'day').format('DD/MM/YYYY')})` : null} />
            <PreviewRow label="Prioridade" value={PRIORITY_LABEL[c.returnPriority]} />
            <PreviewRow label="Observações" value={c.returnNotes} />
          </div>
        )}

        {/* Medicamentos */}
        {c.medications?.length > 0 && (() => {
          const FREQ_TIMES = { once: '1 vez', twice: '2 vezes', three_times: '3 vezes', four_times: '4 vezes' };
          const FREQ_TYPE = { by_day: 'ao dia', by_month: 'ao mês', by_year: 'ao ano' };
          const DUR_TYPE = { days: 'dias', weeks: 'semanas', months: 'meses' };
          return (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
              <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Medicamentos</p>
              {c.medications.map((m, i) => {
                const freq = [FREQ_TIMES[m.frequencyTimes], FREQ_TYPE[m.frequencyType]].filter(Boolean).join(' ');
                const dur = m.durationTimes ? `${m.durationTimes} ${DUR_TYPE[m.durationType] || m.durationType || ''}`.trim() : null;
                const period = m.startDate && m.endDate
                  ? `${dayjs(m.startDate).format('DD/MM')} – ${dayjs(m.endDate).format('DD/MM/YYYY')}`
                  : m.startDate ? `a partir de ${dayjs(m.startDate).format('DD/MM/YYYY')}` : null;
                return (
                  <div key={i} style={{ padding: '12px 14px', background: '#f9fafb', borderRadius: '8px', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                      <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: '#1f2937' }}>{m.name}</p>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', whiteSpace: 'nowrap' }}>{m.dosage}</span>
                    </div>
                    {(freq || dur) && (
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#374151' }}>
                        {[freq, dur ? `por ${dur}` : null].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {period && (
                      <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af' }}>{period}</p>
                    )}
                    {m.notes && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>{m.notes}</p>}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Notas Internas */}
        {c.internalNotes && (
          <div style={{ gridColumn: '1 / -1', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Notas Internas</p>
            <p style={{ margin: 0, fontSize: '14px', color: '#78350F' }}>{c.internalNotes}</p>
          </div>
        )}

        {/* Documentos */}
        {c.Documents?.length > 0 && (
          <div style={{ gridColumn: '1 / -1', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px' }}>
            <p style={{ margin: '0 0 14px', fontSize: '13px', fontWeight: '700', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Documentos</p>
            <div className="mr-documents-list">
              {c.Documents.map((doc) => {
                const iconClass = doc.type === 'IMG' ? 'img' : doc.type === 'PDF' ? 'pdf' : 'doc';
                const badgeColors = { PDF: { bg: '#FEE2E2', color: '#DC2626' }, IMG: { bg: '#EFF6FF', color: '#2563EB' }, DOC: { bg: '#F3F4F6', color: '#374151' } };
                const badge = badgeColors[doc.type];
                return (
                  <div key={doc.id} className="mr-doc-row">
                    <div className={`mr-doc-icon ${iconClass}`}>
                      {iconClass === 'img'
                        ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                        : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                      }
                    </div>
                    <div className="mr-doc-info">
                      <span className="mr-doc-title">{doc.title || doc.fileName}</span>
                      <div className="mr-doc-meta">
                        {badge && <span className={`mr-doc-badge ${iconClass}`}>{doc.type}</span>}
                        <span className="mr-doc-date">{dayjs(doc.createdAt).format('DD/MM/YYYY')}</span>
                        {doc.visibleToTutor && <span className="mr-doc-visible-badge">Visível ao tutor</span>}
                      </div>
                    </div>
                    <div className="mr-doc-actions">
                      {doc.fileUrl && (
                        <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="mr-doc-download" title="Baixar arquivo">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                          <span>Baixar</span>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── Pet Profile ───────────────────────── */
function PetProfile({ entityId }) {
  const { petId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [pet, setPet] = useState(location.state?.pet || null);
  const [activeTab, setActiveTab] = useState('consultations');
  const [consultations, setConsultations] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [pendingDocFile, setPendingDocFile] = useState(null);
  const [docForm, setDocForm] = useState({ title: '', type: '' });
  const [showDocModal, setShowDocModal] = useState(false);
  const [showPetModal, setShowPetModal] = useState(false);
  const photoInputRef = useRef(null);
  const docInputRef = useRef(null);

  const inferDocType = (file) => {
    if (file.type === 'application/pdf') return 'PDF';
    if (/\.(jpg|jpeg|png|webp)$/.test(file.name.toLowerCase()) || file.type.startsWith('image/')) return 'IMG';
    return 'DOC';
  };

  const handleDocFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const inferredType = inferDocType(file);
    const baseName = file.name.replace(/\.[^.]+$/, '');
    setPendingDocFile(file);
    setDocForm({ title: baseName, type: inferredType });
    setShowDocModal(true);
    e.target.value = '';
  };

  const handleDocSubmit = async () => {
    if (!docForm.title.trim()) { toast.error('Título é obrigatório'); return; }
    if (!docForm.type) { toast.error('Tipo é obrigatório'); return; }
    setShowDocModal(false);
    setUploadingDoc(true);
    const result = await medicalRecordService.uploadDocument(pendingDocFile, {
      petId: pet.id,
      entityId,
      title: docForm.title.trim(),
      type: docForm.type,
    });
    setUploadingDoc(false);
    setPendingDocFile(null);
    if (result.success) {
      toast.success('Documento enviado!');
      loadDocuments();
    } else {
      toast.error(result.error || 'Erro ao enviar documento');
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    const result = await medicalRecordService.uploadPetPhoto(file, pet.id, entityId);
    setUploadingPhoto(false);
    if (result.success) {
      setPet((p) => ({ ...p, photo: result.photoUrl }));
      toast.success('Foto atualizada!');
    } else {
      toast.error(result.error || 'Erro ao enviar foto');
    }
    e.target.value = '';
  };
  const [docPage, setDocPage] = useState(1);
  const [consultPage, setConsultPage] = useState(1);

  const loadConsultations = useCallback(async () => {
    if (!pet?.id) return;
    setLoading(true);
    const result = await medicalRecordService.getConsultations({ petId: pet.id, page: consultPage });
    if (result.success) setConsultations(result.consultations);
    setLoading(false);
  }, [pet?.id, consultPage]);

  // Load pet from API if not in location state (e.g. direct URL access)
  useEffect(() => {
    const loadPetData = async () => {
      if (!pet && petId) {
        try {
          const { success, pet: petData } = await medicalRecordService.getPet(petId);
          if (success && petData) {
            setPet(petData);
          } else {
            // Caso a API retorne sucesso mas sem dados do pet
            toast.error('Paciente não encontrado.');
            navigate('/patients');
          }
        } catch (err) {
          const errorMessage = err.response?.data?.msg || err.message || 'Paciente não encontrado.';
          toast.error(errorMessage);
          navigate('/patients');
        }
      }
    }
    loadPetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId]);

  const loadDocuments = useCallback(async () => {
    if (!pet?.id) return;
    setLoading(true);
    const result = await medicalRecordService.getDocuments({ petId: pet.id });
    if (result.success) setDocuments(result.documents);
    setLoading(false);
  }, [pet?.id]);

  useEffect(() => {
    if (activeTab === 'consultations') loadConsultations();
    else if (activeTab === 'documents') loadDocuments();
  }, [activeTab, loadConsultations, loadDocuments]);

  const getDocIcon = (doc) => {
    if (doc.type === 'PDF') return 'pdf';
    if (doc.type === 'IMG') return 'img';
    if (doc.type === 'DOC') return 'doc';
    // fallback por extensão
    const name = (doc.fileName || doc.title || '').toLowerCase();
    if (name.endsWith('.pdf')) return 'pdf';
    if (/\.(jpg|jpeg|png|webp)$/.test(name)) return 'img';
    return 'doc';
  };

  const DOC_TYPE_BADGE = {
    PDF: { label: 'PDF', bg: '#FEE2E2', color: '#DC2626' },
    IMG: { label: 'IMG', bg: '#EFF6FF', color: '#2563EB' },
    DOC: { label: 'DOC', bg: '#F3F4F6', color: '#374151' },
  };

  const petAge = pet?.birthday ? (() => {
    const months = dayjs().diff(dayjs(pet.birthday), 'month');
    if (months < 12) return `${months} ${months === 1 ? 'mês' : 'meses'}`;
    const years = Math.floor(months / 12);
    const rem = months % 12;
    return rem > 0 ? `${years}a ${rem}m` : `${years} ${years === 1 ? 'ano' : 'anos'}`;
  })() : null;

  if (!pet) return <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>Carregando...</div>;

  return (
    <div className="mr-container">
      <div className="mr-page-header">
        <div className="mr-breadcrumb">
          <button className="mr-breadcrumb-link" onClick={() => {
            const owner = pet?.owner;
            if (owner?.id) navigate(`/medical-record/owner/${owner.id}`, { state: { owner } });
            else navigate('/medical-record');
          }}>Tutores</button>
          {pet?.owner && (
            <>
              <span className="mr-breadcrumb-sep">›</span>
              <button className="mr-breadcrumb-link" onClick={() => navigate(`/medical-record/owner/${pet.owner.id}`, { state: { owner: pet.owner } })}>{pet.owner.name}</button>
            </>
          )}
          <span className="mr-breadcrumb-sep">›</span>
          <span className="mr-breadcrumb-current">{pet.name}</span>
        </div>
      </div>

      <div className="mr-profile-layout">
        {/* Main Column */}
        <div>
          <div className="mr-tabs">
            {[['consultations', 'Consultas'], ['documents', 'Documentos']].map(([val, label]) => (
              <button key={val} className={`mr-tab ${activeTab === val ? 'active' : ''}`} onClick={() => setActiveTab(val)}>{label}</button>
            ))}
          </div>

          {/* Consultas */}
          {activeTab === 'consultations' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button className="btn-primary" style={{ padding: '10px 20px' }} onClick={() => navigate(`/medical-record/pet/${petId}/consultation/new`, { state: { pet } })}>
                  + Nova Consulta
                </button>
              </div>
              {loading ? <div style={{ textAlign: 'center', padding: '32px', color: '#999' }}>Carregando...</div> :
                consultations.length === 0 ? (
                  <div className="mr-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                      <rect x="9" y="3" width="6" height="4" rx="1" ry="1" />
                    </svg>
                    <p>Nenhuma consulta registrada</p></div>
                ) : (
                  consultations.map((c) => {
                    const conditionLabel = { good: 'Bom', fair: 'Regular', poor: 'Ruim', critical: 'Crítico' };
                    const conditionColor = { good: { bg: '#DCFCE7', color: '#166534' }, fair: { bg: '#FEF9C3', color: '#854D0E' }, poor: { bg: '#FEE2E2', color: '#991B1B' }, critical: { bg: '#FCE7F3', color: '#9D174D' } };
                    const hasMeta = c.weight || c.generalCondition || c.medications?.length > 0;
                    return (
                      <div key={c.id} className="mr-consultation-card" onClick={() => navigate(`/medical-record/pet/${petId}/consultation/${c.id}`, { state: { pet, consultation: c } })} style={{ cursor: 'pointer' }}>
                        <div className="mr-consultation-header">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} title={c.createdAt}>
                            <span className="mr-consultation-date">{dayjs(c.createdAt).format('DD/MM/YYYY [às] HH:mm')}</span>
                            {c.isDraft && (
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400E', background: '#FEF3C7', padding: '2px 8px', borderRadius: '20px' }}>Rascunho</span>
                            )}
                          </div>
                          <span style={{ fontSize: '13px', color: 'var(--color-main)', fontWeight: '600' }}>Ver detalhes →</span>
                        </div>

                        {c.chiefComplaint ? (
                          <p className="mr-consultation-complaint">{c.chiefComplaint}</p>
                        ) : c.clinicalDiagnosis ? (
                          <p className="mr-consultation-complaint">{c.clinicalDiagnosis}</p>
                        ) : (
                          <p className="mr-consultation-complaint" style={{ color: '#9ca3af', fontStyle: 'italic' }}>Sem descrição registrada</p>
                        )}

                        {c.chiefComplaint && c.clinicalDiagnosis && (
                          <p className="mr-consultation-diagnosis">{c.clinicalDiagnosis}</p>
                        )}

                        {hasMeta && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                            {c.weight && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#374151', background: '#F3F4F6', padding: '2px 8px', borderRadius: '20px' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><path d="M8 12h8M12 8v8" /></svg>
                                {c.weight} kg
                              </span>
                            )}
                            {c.generalCondition && conditionColor[c.generalCondition] && (
                              <span style={{ fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', background: conditionColor[c.generalCondition].bg, color: conditionColor[c.generalCondition].color }}>
                                {conditionLabel[c.generalCondition]}
                              </span>
                            )}
                            {c.medications?.length > 0 && (
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: '600', color: '#5B21B6', background: '#EDE9FE', padding: '2px 8px', borderRadius: '20px' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2v-4M9 21H5a2 2 0 0 1-2-2v-4m0 0h18" /></svg>
                                {c.medications.length} {c.medications.length === 1 ? 'medicamento' : 'medicamentos'}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
            </>
          )}

          {/* Documentos */}
          {activeTab === 'documents' && (
            <>
              {/* Modal de detalhes do documento */}
              {showDocModal && (
                <div className="mr-modal-overlay" onClick={() => setShowDocModal(false)}>
                  <div className="mr-modal" style={{ maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
                    <div className="mr-modal-header">
                      <h2 className="mr-modal-title">Detalhes do documento</h2>
                      <button className="mr-modal-close" onClick={() => setShowDocModal(false)}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    </div>
                    <div className="mr-modal-body">
                      <p style={{ fontSize: '13px', color: '#666', margin: '0 0 16px', wordBreak: 'break-all' }}>
                        Arquivo: <strong>{pendingDocFile?.name}</strong>
                      </p>
                      <div className="cf-field" style={{ marginBottom: '16px' }}>
                        <label className="form-label">Título *</label>
                        <input
                          type="text"
                          className="form-input"
                          value={docForm.title}
                          onChange={(e) => setDocForm((f) => ({ ...f, title: e.target.value }))}
                          placeholder="Ex: Exame de sangue, Raio-X..."
                        />
                      </div>
                      <div className="cf-field" style={{ marginBottom: '24px' }}>
                        <label className="form-label">Tipo *</label>
                        <select
                          className="form-select"
                          value={docForm.type}
                          onChange={(e) => setDocForm((f) => ({ ...f, type: e.target.value }))}
                        >
                          <option value="">Selecionar...</option>
                          <option value="PDF">PDF</option>
                          <option value="IMG">Imagem</option>
                          <option value="DOC">Documento</option>
                        </select>
                      </div>
                      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        <button className="btn-secondary" style={{ padding: '10px 20px' }} onClick={() => setShowDocModal(false)}>Cancelar</button>
                        <button className="btn-primary" style={{ padding: '10px 20px' }} onClick={handleDocSubmit}>Enviar</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                <button
                  className="btn-primary"
                  style={{ padding: '10px 20px' }}
                  onClick={() => docInputRef.current?.click()}
                  disabled={uploadingDoc}
                >
                  {uploadingDoc ? 'Enviando...' : '+ Enviar arquivo'}
                </button>
                <input
                  ref={docInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  style={{ display: 'none' }}
                  onChange={handleDocFileSelected}
                />
              </div>
              {loading ? <div style={{ textAlign: 'center', padding: '32px', color: '#999' }}>Carregando...</div> :
                documents.length === 0 ? (
                  <div className="mr-empty">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                    </svg>
                    <p>Nenhum documento</p></div>
                ) : (
                  <div className="mr-documents-list">
                    {documents.map((doc) => {
                      const iconClass = getDocIcon(doc);
                      const badge = DOC_TYPE_BADGE[doc.type];
                      return (
                        <div key={doc.id} className="mr-doc-row">
                          <div className={`mr-doc-icon ${iconClass}`}>
                            {iconClass === 'img'
                              ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                              : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                            }
                          </div>
                          <div className="mr-doc-info">
                            <span className="mr-doc-title">{doc.title || doc.fileName}</span>
                            <div className="mr-doc-meta">
                              {badge && (
                                <span className={`mr-doc-badge ${iconClass}`}>{badge.label}</span>
                              )}
                              <span className="mr-doc-date">{dayjs(doc.createdAt).format('DD/MM/YYYY')}</span>
                              {doc.visibleToTutor && <span className="mr-doc-visible-badge">Visível ao tutor</span>}
                            </div>
                          </div>
                          <div className="mr-doc-actions">
                            {doc.fileUrl && (
                              <a href={doc.fileUrl} target="_blank" rel="noreferrer" className="mr-doc-download" title="Baixar arquivo">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                <span>Baixar</span>
                              </a>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="mr-pet-sidebar">
          <div className="mr-pet-sidebar-header">
            <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => photoInputRef.current?.click()} title="Clique para alterar a foto">
              {pet.photo ? (
                <img src={pet.photo} alt={pet.name} className="mr-pet-sidebar-photo" onError={(e) => { e.target.src = placeholderImage; }} />
              ) : (
                <div className="mr-pet-photo-placeholder" style={{ width: 100, height: 100 }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="5" /><path d="M12 14c-5 0-8 2.5-8 4v2h16v-2c0-1.5-3-4-8-4z" /></svg>
                </div>
              )}
              <div style={{ position: 'absolute', bottom: 2, right: 2, width: 28, height: 28, borderRadius: '50%', background: uploadingPhoto ? '#ccc' : 'var(--color-main, #7165FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
                {uploadingPhoto
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
                }
              </div>
            </div>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              style={{ display: 'none' }}
              onChange={handlePhotoChange}
              disabled={uploadingPhoto}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <h3 className="mr-pet-sidebar-name" style={{ margin: 0 }}>{pet.name}</h3>
              <button
                title="Editar dados do pet"
                style={{ background: 'none', border: 'none', padding: '3px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#9ca3af', flexShrink: 0 }}
                onClick={() => setShowPetModal(true)}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
              </button>
            </div>
            <p className="mr-pet-sidebar-sub">{SPECIES_LABEL[pet.species] || pet.species} {pet.breed ? `· ${pet.breed}` : ''}</p>
            {petAge && <p className="mr-pet-sidebar-sub">{petAge}</p>}
          </div>

          {(pet.owner || pet.ownerName) && (
            <>
              <hr className="mr-sidebar-divider" />
              <div className="mr-sidebar-section">
                <h4>Tutor</h4>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#1A1A1A', margin: '0 0 4px' }}>{pet.owner?.name || pet.ownerName}</p>
                {(pet.owner?.phone || pet.ownerPhone) && <p style={{ fontSize: '13px', color: '#666', margin: 0 }}>{formatPhone(pet.owner?.phone || pet.ownerPhone)}</p>}
              </div>
            </>
          )}

          {pet.allergies?.length > 0 && (
            <>
              <hr className="mr-sidebar-divider" />
              <div className="mr-sidebar-section">
                <h4>Alergias</h4>
                <div className="mr-chips">
                  {pet.allergies.map((a, i) => <span key={i} className="mr-chip-allergy">{a.name || a}</span>)}
                </div>
              </div>
            </>
          )}

          {pet.activeMedications?.length > 0 && (
            <>
              <hr className="mr-sidebar-divider" />
              <div className="mr-sidebar-section">
                <h4>Medicamentos Ativos</h4>
                <div className="mr-chips">
                  {pet.activeMedications.map((m, i) => <span key={i} className="mr-chip-medication">{m.name || m}</span>)}
                </div>
              </div>
            </>
          )}

          <hr className="mr-sidebar-divider" />
          <button
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={() => navigate(`/medical-record/pet/${petId}/consultation/new`, { state: { pet } })}
          >
            + Iniciar Consulta
          </button>
        </div>
      </div>
      <PetModal
        isOpen={showPetModal}
        onClose={() => setShowPetModal(false)}
        pet={pet}
        ownerId={pet?.owner?.id || pet?.ownerId}
        entityId={entityId}
        onSaved={(updatedPet) => {
          const merged = { ...pet, ...updatedPet };
          setPet(merged);
          navigate(`/medical-record/pet/${petId}`, { state: { pet: merged }, replace: true });
        }}
      />
    </div>
  );
}

/* ─────────────────────── Pet List Level ────────────────────── */
function PetList({ owner: ownerProp, entityId, onBack, onSelectPet }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { ownerId: ownerIdParam } = useParams();
  const [owner, setOwner] = useState(ownerProp ?? location.state?.owner ?? null);
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showPetModal, setShowPetModal] = useState(false);
  const [editingPet, setEditingPet] = useState(null);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [confirmDeletePet, setConfirmDeletePet] = useState(null);
  const [confirmDeleteOwner, setConfirmDeleteOwner] = useState(null);

  useEffect(() => {
    const loadOwnerData = async () => {
      if (!ownerIdParam || !entityId) return;
      try {
        console.log("Loading owner data for ownerIdParam:", ownerIdParam, "entityId:", entityId);
        const result = await medicalRecordService.getOwner(ownerIdParam, entityId);
        if (result.success && result.owner) {
          setOwner(result.owner);
          if (result.owner.pets?.length > 0) setPets(result.owner.pets);
        }
      } catch (err) {
        const errorMessage = err.response?.data?.msg || 'Tutor não encontrado.';
        toast.error(errorMessage);
        navigate('/medical-record');
      }
    };
    loadOwnerData();
  }, [ownerIdParam, entityId, navigate]);

  const load = useCallback(async () => {
    if (!owner) return;
    setLoading(true);
    const result = await medicalRecordService.getPets({ ownerId: owner.id, entityId });
    if (result.success) setPets(result.pets ?? []);
    setLoading(false);
  }, [owner, entityId]);

  useEffect(() => { load(); }, [load]);

  const handleDeleteOwner = async () => {
    if (!confirmDeleteOwner) return;
    const result = await medicalRecordService.deleteOwner(confirmDeleteOwner.id);
    if (result.success) {
      toast.success('Tutor removido com sucesso!');
      onBack();
    } else {
      toast.error(result.error || 'Erro ao remover tutor.');
    }
    setConfirmDeleteOwner(null);
  };
  const handleDeletePet = async () => {
    if (!confirmDeletePet) return;
    const result = await medicalRecordService.deletePet(confirmDeletePet.id);
    if (result.success) {
      toast.success('Pet removido!');
      load();
    } else {
      toast.error(result.error || 'Erro ao remover pet');
    }
    setConfirmDeletePet(null);
  };


  if (!owner) return <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>Carregando...</div>;

  const filteredPets = pets.filter((p) =>
    !search || p.name?.toLowerCase().includes(search.toLowerCase()) ||
    p.breed?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mr-container">
      {/* Header */}
      <div className="mr-tutores-header">
        <div>
          <div className="mr-breadcrumb" style={{ marginBottom: '16px' }}>
            <button className="mr-breadcrumb-link" onClick={onBack}>Tutores</button>
            <span className="mr-breadcrumb-sep">›</span>
            <span className="mr-breadcrumb-current">{owner?.name}</span>
          </div>
          <h1 className="mr-tutores-title">{owner?.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '6px', flexWrap: 'wrap' }}>
            {owner?.phone && (
              <span
                title="Clique para copiar"
                onClick={() => {
                  navigator.clipboard.writeText(owner.phone.replace('+55', ''));
                  toast.success('Telefone copiado!');
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#6b7280', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(113,101,255,0.08)'; e.currentTarget.style.color = 'var(--color-main)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7165FF" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l1.45-1.45a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                {formatPhone(owner.phone)}
              </span>
            )}
            {owner?.email && (
              <span
                title="Clique para copiar"
                onClick={() => { navigator.clipboard.writeText(owner.email); toast.success('E-mail copiado!'); }}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#6b7280', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', transition: 'background 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(113,101,255,0.08)'; e.currentTarget.style.color = 'var(--color-main)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6b7280'; }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                {owner.email}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button className="mr-action-btn" title="Editar tutor" onClick={() => setShowOwnerModal(true)}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
          </button>
          <button
            className="mr-action-btn danger" title="Remover tutor"
            onClick={() => setConfirmDeleteOwner(owner)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
          </button>
          <button
            onClick={() => { setEditingPet(null); setShowPetModal(true); }}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            Novo Pet
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mr-tutores-filters">
        <div className="mr-search mr-tutores-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input
            type="text"
            placeholder="Buscar por nome ou raça..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '18px', lineHeight: 1, padding: 0 }}>×</button>}
        </div>
        <span style={{ fontSize: '13px', color: '#9ca3af', flexShrink: 0 }}>
          {pets.length} {pets.length === 1 ? 'paciente' : 'pacientes'}
        </span>
      </div>

      {/* Table */}
      <div className="mr-tutores-table-wrapper">
        <table className="mr-tutores-table">
          <thead>
            <tr>
              <th>Paciente</th>
              <th>Espécie</th>
              <th>Raça / Idade</th>
              <th>Última Visita</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '48px', color: '#999' }}>Carregando pacientes...</td></tr>
            ) : pets.length === 0 ? (
              <tr><td colSpan={6}>
                <div className="mr-empty">
                  <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="5" /><path d="M12 14c-5 0-8 2.5-8 4v2h16v-2c0-1.5-3-4-8-4z" /></svg>
                  <p style={{ fontWeight: '600', marginTop: '8px' }}>Nenhum paciente cadastrado</p>
                  <p style={{ fontSize: '14px', opacity: 0.7 }}>Adicione o primeiro pet deste tutor</p>
                </div>
              </td></tr>
            ) : filteredPets.length === 0 ? (
              <tr><td colSpan={6}>
                <div className="mr-empty">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                  <p style={{ fontWeight: '600', marginTop: '8px' }}>Nenhum resultado para "{search}"</p>
                </div>
              </td></tr>
            ) : filteredPets.map((pet) => {
              const age = pet.birthday ? (() => {
                const m = dayjs().diff(dayjs(pet.birthday), 'month');
                if (m < 12) return `${m} ${m === 1 ? 'mês' : 'meses'}`;
                return `${Math.floor(m / 12)} anos`;
              })() : null;
              return (
                <tr key={pet.id} className="mr-tutores-row" onClick={() => navigate(`/medical-record/pet/${pet.id}`, { state: { pet: { ...pet, owner } } })}>
                  {/* Paciente */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {pet.photo ? (
                        <img src={pet.photo} alt={pet.name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.src = placeholderImage; }} />
                      ) : (
                        <div className="mr-owner-avatar" style={{ width: 44, height: 44, fontSize: '18px' }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="5" /><path d="M12 14c-5 0-8 2.5-8 4v2h16v-2c0-1.5-3-4-8-4z" /></svg>
                        </div>
                      )}
                      <div>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#1A1A1A' }}>{pet.name}</div>
                        {pet.color && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{pet.color}</div>}
                      </div>
                    </div>
                  </td>
                  {/* Espécie */}
                  <td>
                    <span style={{ fontSize: '13px', color: '#374151', fontWeight: '500' }}>
                      {SPECIES_LABEL[pet.species] || pet.species || '—'}
                    </span>
                  </td>
                  {/* Raça / Idade */}
                  <td>
                    <div style={{ fontSize: '13px', color: '#374151' }}>{pet.breed || '—'}</div>
                    {age && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{age}</div>}
                  </td>
                  {/* Última Visita */}
                  <td>
                    {pet.lastVisitAt ? (
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{dayjs(pet.lastVisitAt).format('DD MMM, YYYY')}</div>
                        <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{dayjs(pet.lastVisitAt).fromNow()}</div>
                      </div>
                    ) : <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>}
                  </td>
                  {/* Status */}
                  <td>
                    <span className={`mr-status-badge ${pet.active !== false ? 'active' : 'inactive'}`}>
                      {pet.active !== false ? 'ATIVO' : 'INATIVO'}
                    </span>
                  </td>
                  {/* Ações */}
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <button className="mr-action-btn" title="Ver prontuário"
                        onClick={() => navigate(`/medical-record/pet/${pet.id}`, { state: { pet: { ...pet, owner } } })}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      </button>
                      <button className="mr-action-btn" title="Editar pet"
                        onClick={() => { setEditingPet(pet); setShowPetModal(true); }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                      </button>
                      <button className="mr-action-btn danger" title="Remover pet"
                        onClick={() => setConfirmDeletePet(pet)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <OwnerModal
        isOpen={showOwnerModal}
        onClose={() => setShowOwnerModal(false)}
        owner={owner}
        entityId={entityId}
        onSaved={(updatedOwner) => {
          setOwner(updatedOwner);
          navigate(`/medical-record/owner/${updatedOwner.id}`, { state: { owner: updatedOwner }, replace: true });
        }}
      />
      <PetModal
        isOpen={showPetModal}
        onClose={() => setShowPetModal(false)}
        pet={editingPet}
        ownerId={owner?.id}
        entityId={entityId}
        onSaved={() => { load(); }}
      />
      <ConfirmModal
        isOpen={!!confirmDeletePet}
        onClose={() => setConfirmDeletePet(null)}
        onConfirm={handleDeletePet}
        title="Remover Pet"
        message={`Tem certeza que deseja remover o pet "${confirmDeletePet?.name}"? Todo o seu histórico médico será removido permanentemente.`}
        confirmText="Remover"
        type="danger"
      />
      <ConfirmModal
        isOpen={!!confirmDeleteOwner}
        onClose={() => setConfirmDeleteOwner(null)}
        onConfirm={handleDeleteOwner}
        title="Remover Tutor"
        message={`Tem certeza que deseja remover o tutor "${confirmDeleteOwner?.name}"? Todos os pets e o histórico médico vinculados a ele serão removidos permanentemente.`}
        confirmText="Remover"
        type="danger"
      />
    </div>
  );
}

/* ──────────────── Consultation Form Wrapper ────────────────── */
function ConsultationFormWrapper({ entityId }) {
  const { petId, consultationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pet = location.state?.pet;
  const scheduleRef = location.state?.scheduleRef;

  const goToPet = () => navigate(`/medical-record/pet/${petId}`, { state: { pet } });

  return (
    <div className="mr-container">
      <div className="mr-page-header">
        <div className="mr-breadcrumb">
          <button className="mr-breadcrumb-link" onClick={() => navigate('/medical-record')}>Tutores</button>
          {pet?.owner && (
            <>
              <span className="mr-breadcrumb-sep">›</span>
              <button className="mr-breadcrumb-link" onClick={() => navigate(`/medical-record/owner/${pet.owner.id}`, { state: { owner: pet.owner } })}>{pet.owner.name}</button>
            </>
          )}
          {pet && (
            <>
              <span className="mr-breadcrumb-sep">›</span>
              <button className="mr-breadcrumb-link" onClick={goToPet}>{pet.name}</button>
            </>
          )}
          <span className="mr-breadcrumb-sep">›</span>
          <span className="mr-breadcrumb-current">{consultationId ? 'Editar Consulta' : 'Nova Consulta'}</span>
        </div>
      </div>
      <ConsultationForm
        petId={petId}
        entityId={entityId}
        consultationId={consultationId}
        scheduleRef={!consultationId ? scheduleRef : null}
        onSaved={(saved) => navigate(`/medical-record/pet/${petId}/consultation/${saved?.id || consultationId}`, { state: { pet } })}
        onCancel={goToPet}
      />
    </div>
  );
}

/* ─────────────── Consultation Preview Wrapper ──────────────── */
function ConsultationPreviewWrapper({ entityId }) {
  const { petId, consultationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState(null);
  const [finalizing, setFinalizing] = useState(false);
  const pet = location.state?.pet;

  useEffect(() => {
    if (consultationId) {
      medicalRecordService.getConsultation(consultationId).then((r) => {
        if (r.success && r.consultation) setConsultation(r.consultation);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consultationId]);

  const goToPet = () => navigate(`/medical-record/pet/${petId}`, { state: { pet } });

  const handleFinalize = async () => {
    setFinalizing(true);
    const result = await medicalRecordService.updateConsultation(consultationId, { isDraft: false, active: true, entityId });
    setFinalizing(false);
    if (result.success) {
      toast.success('Consulta finalizada!');
      setConsultation((prev) => ({ ...prev, isDraft: false }));
    } else {
      toast.error(result.error || 'Erro ao finalizar consulta');
    }
  };

  const handleDelete = async () => {
    const result = await medicalRecordService.deleteConsultation(consultationId);
    if (result.success) {
      toast.success('Consulta removida!');
      goToPet();
    } else {
      toast.error(result.error || 'Erro ao remover consulta');
    }
  };

  if (!consultation) return <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>Carregando...</div>;

  return (
    <ConsultationPreview
      consultation={consultation}
      finalizing={finalizing}
      onBack={goToPet}
      onEdit={() => navigate(`/medical-record/pet/${petId}/consultation/${consultationId}/edit`, { state: { pet, consultation } })}
      onFinalize={handleFinalize}
      onDelete={handleDelete}
    />
  );
}

/* ─────────────────── Owner List (Level 1) ──────────────────── */
function MedicalRecord({ scheduleRef, onScheduleRefConsumed, viewScheduleRef, onViewScheduleRefConsumed }) {
  const { ownerId, petId, consultationId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [entity, setEntity] = useState(null);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('recent');
  const [linesPerPage, setLinesPerPage] = useState(20);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [confirmDeleteOwner, setConfirmDeleteOwner] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeleteOwner = async () => {
    if (!confirmDeleteOwner) return;
    const result = await medicalRecordService.deleteOwner(confirmDeleteOwner.id);
    if (result.success) {
      toast.success('Tutor removido!');
      loadOwners();
    } else {
      toast.error(result.error || 'Erro ao remover tutor');
    }
    setConfirmDeleteOwner(null);
  };

  const LIMIT = linesPerPage;

  useEffect(() => {
    const loadEntity = async () => {
      const result = await entityService.getMyEntities();
      if (result.success && result.entities?.length > 0) {
        setEntity(result.entities[0]);
      }
    };
    loadEntity();
  }, []);

  const loadOwners = useCallback(async () => {
    if (!entity) return;
    setLoading(true);
    const result = await medicalRecordService.getOwners({ entityId: entity.id, search, page, limit: LIMIT });
    if (result.success) {
      setOwners(result.owners);
      setTotal(result.total || 0);
    } else {
      toast.error(result.error || 'Erro ao carregar tutores');
    }
    setLoading(false);
  }, [entity, search, page]);

  useEffect(() => {
    if (entity) loadOwners();
  }, [loadOwners, entity]);

  // Reset page on search
  useEffect(() => { setPage(1); }, [search]);

  // Handle scheduleRef from agenda (Iniciar Atendimento)
  useEffect(() => {
    if (scheduleRef?.pet?.id) {
      navigate(`/medical-record/pet/${scheduleRef.pet.id}/consultation/new`, {
        state: { pet: { ...scheduleRef.pet, owner: scheduleRef.owner }, scheduleRef },
        replace: true,
      });
      onScheduleRefConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleRef]);

  // Handle viewScheduleRef from agenda (Ver Atendimento)
  useEffect(() => {
    if (viewScheduleRef) {
      if (viewScheduleRef.pet?.id && viewScheduleRef.consultationId) {
        const load = async () => {
          // Prioriza o consultationId que já vem no agendamento
          const consultationId = viewScheduleRef.consultationId;
          const petId = viewScheduleRef.pet.id;

          const fullConsultationResult = await medicalRecordService.getConsultation(consultationId);
          if (fullConsultationResult.success && fullConsultationResult.consultation) {
            navigate(`/medical-record/pet/${petId}/consultation/${consultationId}`, {
              state: { pet: { ...viewScheduleRef.pet, owner: viewScheduleRef.owner }, consultation: fullConsultationResult.consultation },
              replace: true,
            });
          } else {
            toast.error('Não foi possível carregar os detalhes da consulta.');
          }
          onViewScheduleRefConsumed?.();
        };
        load();
      } else if (!viewScheduleRef.pet?.id && viewScheduleRef.id) {
        navigate(`/appointment/${viewScheduleRef.id}`);
        onViewScheduleRefConsumed?.();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewScheduleRef]);

  const isNew = location.pathname.endsWith('/consultation/new');
  const isEdit = location.pathname.endsWith('/edit');

  const sortedOwners = React.useMemo(() => {
    const list = [...owners];
    if (sortOrder === 'name_asc') return list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
    if (sortOrder === 'name_desc') return list.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'pt-BR'));
    if (sortOrder === 'oldest') return list.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    return list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)); // 'recent'
  }, [owners, sortOrder]);

  if (petId && consultationId && isEdit) return <ConsultationFormWrapper entityId={entity?.id} />;
  if (petId && consultationId) return <ConsultationPreviewWrapper entityId={entity?.id} />;
  if (petId && isNew) return <ConsultationFormWrapper entityId={entity?.id} />;
  if (petId) return <PetProfile entityId={entity?.id} />;
  if (ownerId) return <PetList entityId={entity?.id} onBack={() => navigate('/medical-record')} />;

  const totalPages = Math.ceil(total / LIMIT);
  const startItem = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const endItem = Math.min(page * LIMIT, total);

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="mr-container">
      {/* Page Header */}
      <div className="mr-tutores-header">
        <div>
          <h1 className="mr-tutores-title">Gestão de Tutores</h1>
          <p className="mr-tutores-subtitle">Gerencie os responsáveis pelos pacientes da clínica Vetsse.</p>
        </div>
        <button
          onClick={() => { setEditingOwner(null); setShowOwnerModal(true); }}
          style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', border: 'none', borderRadius: '10px', background: 'var(--color-main)', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
          Novo Tutor
        </button>
      </div>

      {/* Filters Bar */}
      <div className="mr-tutores-filters">
        <div className="mr-search mr-tutores-search">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input
            type="text"
            placeholder="Buscar por nome, CPF ou telefone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="mr-filter-group">
          <span className="mr-filter-label">ORDENAR:</span>
          <select
            className="mr-filter-select-native"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="recent">Mais recentes</option>
            <option value="oldest">Mais antigos</option>
            <option value="name_asc">A → Z</option>
            <option value="name_desc">Z → A</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="mr-tutores-table-wrapper">
        <table className="mr-tutores-table">
          <thead>
            <tr>
              <th>Nome do Tutor</th>
              <th>Contato</th>
              <th>Pets</th>
              <th>Última Visita</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '48px', color: '#999' }}>Carregando tutores...</td></tr>
            ) : owners.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="mr-empty">
                    <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
                    <p style={{ fontWeight: '600', marginTop: '8px' }}>Nenhum tutor encontrado</p>
                    <p style={{ fontSize: '14px', opacity: 0.7 }}>{search ? `Nenhum resultado para "${search}"` : 'Adicione o primeiro tutor'}</p>
                  </div>
                </td>
              </tr>
            ) : sortedOwners.map((owner) => (
              <tr key={owner.id} className="mr-tutores-row" onClick={() => navigate(`/medical-record/owner/${owner.id}`, { state: { owner } })}>
                {/* Nome do Tutor */}
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {owner.photo ? (
                      <img src={owner.photo} alt={owner.name} style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={(e) => { e.target.src = placeholderImage; }} />
                    ) : (
                      <div className="mr-owner-avatar" style={{ width: 44, height: 44, fontSize: '16px' }}>{(owner.name || '?')[0].toUpperCase()}</div>
                    )}
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '14px', color: '#1A1A1A' }}>{owner.name}</div>
                      {owner.cpf && <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>CPF: {formatCPF(owner.cpf)}</div>}
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>Desde {dayjs(owner.createdAt).format('MMM YYYY')}</div>
                    </div>
                  </div>
                </td>
                {/* Contato */}
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {owner.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7165FF" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l1.45-1.45a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                        {formatPhone(owner.phone)}
                      </div>
                    )}
                    {owner.email && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>
                        {owner.email}
                      </div>
                    )}
                    {!owner.phone && !owner.email && <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>}
                  </div>
                </td>
                {/* Pets */}
                <td>
                  {owner.petsCount > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span className="mr-pets-count-badge">{owner.petsCount} {owner.petsCount === 1 ? 'pet' : 'pets'}</span>
                      {owner.pets?.length > 0 && (
                        <span style={{ fontSize: '13px', color: '#6b7280' }}>({owner.pets.map(p => p.name).join(', ')})</span>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>
                  )}
                </td>
                {/* Última Visita */}
                <td>
                  {owner.lastConsultationAt ? (
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{dayjs(owner.lastConsultationAt).format('DD MMM, YYYY')}</div>
                      <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>{dayjs(owner.lastConsultationAt).fromNow()}</div>
                    </div>
                  ) : (
                    <span style={{ color: '#9ca3af', fontSize: '13px' }}>—</span>
                  )}
                </td>
                {/* Status */}
                <td>
                  <span className="mr-status-badge active">ATIVO</span>
                </td>
                {/* Ações */}
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      className="mr-action-btn"
                      title="Ver tutor"
                      onClick={() => navigate(`/medical-record/owner/${owner.id}`, { state: { owner } })}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    </button>
                    <button
                      className="mr-action-btn"
                      title="Editar tutor"
                      onClick={() => { setEditingOwner(owner); setShowOwnerModal(true); }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                    </button>
                    <button className="mr-action-btn danger" title="Remover tutor"
                      onClick={() => setConfirmDeleteOwner(owner)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="mr-tutores-pagination">
          <span className="mr-pagination-info">Mostrando <strong>{startItem} - {endItem}</strong> de <strong>{total}</strong> tutores</span>
          <div className="mr-pagination-pages">
            <button className="mr-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
            {getPageNumbers().map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="mr-page-ellipsis">...</span>
              ) : (
                <button key={p} className={`mr-page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              )
            )}
            <button className="mr-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          </div>
          <div className="mr-lines-per-page">
            <span>Linhas por página:</span>
            <input
              type="number"
              min="5"
              max="100"
              value={linesPerPage}
              onChange={(e) => { const v = parseInt(e.target.value); if (v >= 5) { setLinesPerPage(v); setPage(1); } }}
            />
          </div>
        </div>
      )}

      <OwnerModal
        isOpen={showOwnerModal}
        onClose={() => setShowOwnerModal(false)}
        owner={editingOwner}
        entityId={entity?.id}
        onSaved={() => {
          loadOwners();
        }}
      />
      <ConfirmModal
        isOpen={!!confirmDeleteOwner}
        onClose={() => setConfirmDeleteOwner(null)}
        onConfirm={handleDeleteOwner}
        title="Remover Tutor"
        message={`Tem certeza que deseja remover o tutor "${confirmDeleteOwner?.name}"? Todos os pets e histórico médico vinculados serão removidos permanentemente.`}
        confirmText="Remover"
        type="danger"
      />
    </div>
  );
}

export default MedicalRecord;
